'use client'

import { FileText } from "lucide-react"
import { navigateUrl } from "@/utils/client-request"
import { getVendorPhones } from "@/utils/vendorPhones"
import { usePathname } from "next/navigation"
import EmptyState from "../elements/EmptyState"

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const sweepLink = `pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${focusRing}`

export default function About({ vendor }: { vendor?: any }) {
  const pathname = usePathname()
  const phones = getVendorPhones(vendor)
  const vendorName = vendor?.name || 'Дэлгүүр'
  const aboutHtml: string | null = vendor?.about || null
  const hasContact = !!(phones.length > 0 || vendor?.email || vendor?.address)

  return (
    <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-20">
      <nav aria-label="Breadcrumb" className="flex items-center gap-x-1.5 text-xs text-muted-foreground">
        <a href={navigateUrl('/', pathname)} className={`transition-colors duration-150 hover:text-foreground ${focusRing}`}>Нүүр</a>
        <span aria-hidden>/</span>
      </nav>

      <header className="mt-8 lg:mt-16 max-w-4xl">
        <div className={eyebrow}>Бидний тухай</div>
        <h1 className="mt-4 text-4xl sm:text-6xl tracking-tight text-foreground">{vendorName}</h1>
        {vendor?.description && (
          <p className="mt-6 max-w-2xl text-lg sm:text-xl leading-relaxed text-muted-foreground">{vendor.description}</p>
        )}
      </header>

      <div className="mt-16 lg:mt-24 pt-10 border-t border-border lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-20">
        <aside className="mb-12 lg:mb-0 lg:sticky lg:top-24 lg:self-start space-y-10">
          {hasContact && (
            <section>
              <h2 className={eyebrow}>Холбоо барих</h2>
              <ul className="mt-4 space-y-2 text-[15px]">
                {phones.map(p => <li key={p}><a href={`tel:${p}`} className={sweepLink}>{p}</a></li>)}
                {vendor?.email && <li><a href={`mailto:${vendor.email}`} className={`break-all ${sweepLink}`}>{vendor.email}</a></li>}
                {vendor?.address && <li className="leading-relaxed text-muted-foreground">{vendor.address}</li>}
              </ul>
            </section>
          )}
          <section>
            <h2 className={eyebrow}>Дэлгүүр</h2>
            <ul className="mt-4 space-y-2 text-[15px]">
              <li><a href={navigateUrl('/product', pathname)} className={sweepLink}>Бүх бүтээгдэхүүн</a></li>
              <li><a href={navigateUrl('/contact', pathname)} className={sweepLink}>Холбоо барих</a></li>
            </ul>
          </section>
        </aside>

        {aboutHtml ? (
          <article
            className="prose prose-neutral max-w-3xl text-foreground prose-headings:font-normal prose-headings:tracking-tight prose-headings:text-foreground prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-5 prose-h3:text-xl prose-p:text-[15px] sm:prose-p:text-base prose-p:leading-[1.8] prose-p:text-foreground/80 prose-li:text-foreground/80 prose-strong:text-foreground prose-strong:font-medium prose-a:text-foreground prose-a:underline-offset-4 prose-a:decoration-1 prose-img:w-full prose-img:my-12 prose-img:rounded-none prose-blockquote:border-l prose-blockquote:border-foreground prose-blockquote:font-normal prose-blockquote:not-italic prose-blockquote:text-foreground prose-hr:border-border"
            dangerouslySetInnerHTML={{ __html: aboutHtml }}
          />
        ) : (
          <EmptyState icon={FileText} title="Мэдээлэл нэмэгдээгүй байна" description="Одоогоор бидний тухай мэдээлэл нэмэгдээгүй байна." />
        )}
      </div>
    </div>
  )
}
